import React, { useState, useEffect } from 'react';
import { fetchSponsors } from './sponsorService';
import './SponsorsCarousel.css';

type Sponsor = {
  id: string;
  brandName: string;
  link: string;
  altText?: string;
  logo?: {
    url?: string;
  };
};

const SponsorsCarousel = () => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  // No fallback sponsors - only show when real data is available

  useEffect(() => {
    const loadSponsors = async () => {
      try {
        setLoading(true);
        const fetchedSponsors = await fetchSponsors();
        if (fetchedSponsors && fetchedSponsors.length > 0) {
          setSponsors(fetchedSponsors);
        } else {
          // No active sponsors - keep empty array
          setSponsors([]);
        }
      } catch (err) {
        console.error('Error loading sponsors:', err);
        // No sponsors on error - keep empty array
        setSponsors([]);
      } finally {
        setLoading(false);
      }
    };

    loadSponsors();
  }, []);

  // Don't render if loading or no sponsors
  if (loading) return null;
  if (!sponsors || sponsors.length === 0) return null;

  // For infinite loop: create many more copies for ultra seamless loop
  const duplicatedSponsors = sponsors.length >= 2 ? [...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors, ...sponsors] : sponsors;

  // Determine CSS class based on sponsor count
  const getTrackClassName = () => {
    if (sponsors.length === 1) return "sponsors-track flex items-center justify-center w-auto max-w-[300px] mx-auto";
    return "sponsors-track flex w-max items-center gap-[3rem]";
  };

  return (
    <section className="mx-auto my-[3rem] mb-[4rem] max-w-[2000px] overflow-hidden px-[2rem] text-center max-[480px]:my-[1.5rem] min-[481px]:max-[767px]:my-[2rem] min-[481px]:max-[767px]:px-[1rem]">
      <h2 className="mb-[2rem] text-center text-[2.5rem] font-[700] text-post-heading max-[480px]:mb-[1rem] max-[480px]:text-[1.8rem] min-[481px]:max-[767px]:mb-[1.5rem] min-[481px]:max-[767px]:text-[2rem]">Sponsors</h2>
      <div className="relative mx-auto w-[40%] max-w-[400px] overflow-hidden">
        <div className={getTrackClassName()}>
          {duplicatedSponsors.map((sponsor, index) => (
            <div key={`${sponsor.id}-${index}`} className="flex shrink-0 items-center justify-center">
              <a 
                href={sponsor.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block no-underline transition-all duration-300 hover:scale-[1.05] hover:opacity-80"
                aria-label={`Visit ${sponsor.brandName} website`}
              >
                <img 
                  src={sponsor.logo && sponsor.logo.url ? sponsor.logo.url : "/images/sponsors/placeholder-logo.svg"} 
                  alt={sponsor.altText || `${sponsor.brandName} logo`}
                  className="h-[60px] w-auto max-w-[200px] object-contain max-[480px]:h-[35px] max-[480px]:max-w-[120px] min-[481px]:max-[767px]:h-[40px] min-[481px]:max-[767px]:max-w-[150px]"
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    // Fallback to a placeholder if logo fails to load
                    e.currentTarget.src = "/images/sponsors/placeholder-logo.svg";
                  }}
                />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SponsorsCarousel;
